<script>
  import route from "@helpers/route"
  import { router } from "@inertiajs/svelte"
  import { Page, PageTitle } from "@objects/Page"
  import { onMount } from "svelte"
  import { addedToCartDialog, merchant } from "@store"
  import { toHuman } from "@helpers/formatters/currency"
  import ContentContainer from "@objects/ContentContainer.svelte"
  import Card from "@components/Card.svelte"
  import Wrap from "@objects/Wrap.svelte"
  import ProposalProductCard from "./ProposalProductCard.svelte"
  import SubmitButton from "@components/SubmitButton.svelte"
  import ShippingFeeIncludedCaption from "../../ShippingFeeIncludedCaption.svelte"
  import AddedToCartDialog from "../AddedToCartDialog.svelte"
  import AffirmPromotionalMessaging, { AffirmPageType } from "@components/AffirmPromotionalMessaging.svelte"
  import LoadNextPage from "@objects/LoadNextPage.svelte"
  import IntersectionObserver from "@objects/IntersectionObserver.svelte"
  import Button from "@components/Button.svelte"

  export let collection
  export let products
  export let productCount
  export let subtotal
  export let total
  export let items

  $: hasPurchasableProducts = products?.data.length > 0

  let processing = false

  const nextOpts = {
    onComplete: () => {
      setTimeout(() => {
        window.affirm?.ui?.refresh?.()
      }, 0)
    },
  }

  function submit() {
    processing = true
    router.post(route("shop.cart.proposal-application.store"), {
      collection_id: collection.id,
    })
    processing = false
  }

  onMount(() => {
    addedToCartDialog.close()
  })
</script>

<svelte:head>
  <title>{collection.name} Proposal</title>
</svelte:head>

<AddedToCartDialog />

<Page>
  <Wrap>
    <ContentContainer class="[ flex gap-12 flex-wrap lg:flex-nowrap ]">
      <section class="[ flex flex-col w-full ]">
        <header class="[ grid gap-1.5 mb-6 ]">
          {#if collection.cover_photo_url}
            <img src={collection.cover_photo_url} alt="{collection.name} banner" />
          {/if}

          <PageTitle class="[ mt-4 ]">{collection.name} Proposal</PageTitle>
          <span class="font-semibold">{productCount} products</span>

          <p>
            Your designer is proposing the following products. To add products to your shopping cart, select "add all to
            cart" in the summary, or hover over a product to add it individually. Product quantities can be adjusted in
            your shopping cart. Prices shown include threshold delivery. White glove delivery and assembly options are
            available during checkout for an additional fee.
          </p>
        </header>

        <div class="[ mt-4 ]">
          <div class="[ grid gap-x-6 gap-y-10 grid-cols-2 md:grid-cols-3 ]">
            {#each products.data as product (product.id)}
              <ProposalProductCard {product} {collection} {items} />
            {/each}
          </div>
        </div>

        <LoadNextPage bind:items={products} let:isLoading let:onLastPage let:next>
          <IntersectionObserver on:intersect={next(nextOpts)} bottom="200%">
            <div class="[ mt-12 flex justify-center ]">
              {#if onLastPage}
                <p class="[ text-sm text-center italic ]">All your products have been loaded</p>
              {:else if isLoading}
                <p class="[ text-sm text-center italic ]">Loading more products…</p>
              {:else}
                <Button on:click={next(nextOpts)} type="neutral">Load more products</Button>
              {/if}
            </div>
          </IntersectionObserver>
        </LoadNextPage>
      </section>

      <aside class="[ w-full lg:w-[19rem] ]">
        <Card type="none" class="pb-8 bg-themebg-50">
          <h2 class="[ text-3xl mb-6 text-center ]">Summary</h2>

          <form on:submit|preventDefault={submit}>
            <div class="[ grid gap-4 ]">
              <div>
                <div class="[ flex items-center justify-between ]">
                  <span>Sub-total</span>
                  <span class="[ text-lg tracking-tight ]">{toHuman(subtotal)}</span>
                </div>

                <div class="[ mt-1.5 ]">
                  <ShippingFeeIncludedCaption />
                </div>
              </div>

              <div class="[ flex justify-between mt-2 ]">
                <span>Sales tax</span>
                <span class="[ tracking-tight ]">Calculated at next step</span>
              </div>

              <div class="[ flex items-center justify-between mt-2 ]">
                <span>Total</span>
                <span class="[ text-lg ]">{toHuman(total)}</span>
              </div>
            </div>

            <div class="[ border-t mt-8 ]">
              <SubmitButton
                disabled={processing || !hasPurchasableProducts}
                {processing}
                class="[ mt-6 w-full ] [ text-base lg:text-sm font-semibold whitespace-nowrap ]"
              >
                Add all to cart and check out &rarr;
              </SubmitButton>

              <p class="[ mt-2 text-sm text-right text-neutral-600 ]">Powered by SideDoor</p>
            </div>

            {#if $merchant.show_affirm_product_promotional_messaging && !collection.settings.hide_affirm_product_promotional_messaging}
              <AffirmPromotionalMessaging
                class="font-sans text-sm mt-6"
                amountInCents={Math.round(total * 100)}
                pageType={AffirmPageType.Search}
              />
            {/if}
          </form>
        </Card>
      </aside>
    </ContentContainer>
  </Wrap>
</Page>
